import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage, visit } from "@hotwired/turbo"
import { acknowledgeLifecycleActivation, lifecycleActivation } from "./lifecycle_activation_acceptance"
import { signInResponse, validMultiTargetStreamResponse } from "./multi_target_revalidation_stream"

const ACCEPT = "text/vnd.turbo-stream.html, text/html;q=0.9"
const EXPECTED_STREAMS = [
  { target: "suppliers_list", action: "update", method: null },
  { target: "wallet_hud", action: "update", method: "morph" },
  { target: "profile_affordance", action: "update", method: "morph" }
]

export default class extends Controller {
  static values = {
    timeout: { type: Number, default: 5_000 },
    url: String
  }

  connect() {
    this.desiredGeneration = 0
    this.inFlight = null
    this.pendingIntent = null
    this.submissionHeld = false
    this.acceptedLifecycleIntentId ??= null
  }

  disconnect() {
    this.invalidate()
  }

  activate(event) {
    const activation = lifecycleActivation(event, this.acceptedLifecycleIntentId)
    if (!activation) return
    if (activation.duplicate) {
      activation.acknowledge()
      return
    }
    if (this.held()) {
      if (this.inFlight) this.invalidate()
      return
    }

    const intent = { generation: ++this.desiredGeneration }
    this.pendingIntent = intent
    this.flush()
    if (this.pendingIntent === intent || this.inFlight === intent) {
      acknowledgeLifecycleActivation(this, activation)
    }
  }

  submitStart(event) {
    if (!this.element.contains(event.target)) return

    this.submissionHeld = true
    this.invalidate()
  }

  submitEnd() {
    this.submissionHeld = false
  }

  flush() {
    if (this.inFlight || !this.pendingIntent) return

    const target = this.listTarget()
    if (!target || !this.hasUrlValue || this.held(target)) {
      this.pendingIntent = null
      return
    }

    const intent = this.pendingIntent
    this.pendingIntent = null
    this.start(intent, target)
  }

  start(intent, target) {
    intent.url = this.urlValue
    intent.abortController = new AbortController()
    intent.timedOut = false
    this.inFlight = intent
    this.setBusy(target, true)
    intent.timeoutId = window.setTimeout(() => this.timeout(intent, target), this.timeoutValue)

    fetch(intent.url, {
      credentials: "same-origin",
      redirect: "follow",
      headers: { Accept: ACCEPT },
      signal: intent.abortController.signal
    })
      .then((response) => this.handleResponse(response, intent))
      .catch(() => {})
      .finally(() => this.finish(intent, target))
  }

  async handleResponse(response, intent) {
    if (signInResponse(response)) {
      this.pendingIntent = null
      this.setBusy(this.listTarget(), false)
      visit(response.url, { action: "replace" })
      return
    }
    if (!response.ok) return

    const body = await response.text()
    if (!this.applicable(intent)) return
    if (!validMultiTargetStreamResponse(response, body, EXPECTED_STREAMS)) return
    if (!this.applicable(intent) || this.held(this.listTarget())) return

    renderStreamMessage(body)
  }

  applicable(intent) {
    return this.inFlight === intent && !intent.timedOut &&
      intent.generation === this.desiredGeneration && this.urlValue === intent.url &&
      Boolean(this.listTarget())
  }

  timeout(intent, target) {
    if (this.inFlight !== intent) return

    intent.timedOut = true
    intent.abortController.abort()
    this.setBusy(target, false)
  }

  finish(intent, target) {
    if (this.inFlight !== intent) return

    window.clearTimeout(intent.timeoutId)
    this.setBusy(target, false)
    this.inFlight = null
    this.flush()
  }

  invalidate() {
    this.desiredGeneration += 1
    this.pendingIntent = null
    if (!this.inFlight) return

    const intent = this.inFlight
    this.inFlight = null
    window.clearTimeout(intent.timeoutId)
    intent.abortController.abort()
    this.setBusy(this.listTarget(), false)
  }

  held(target = this.listTarget()) {
    if (!target || document.hidden || this.submissionHeld) return true
    if (target.hasAttribute("busy") || this.element.querySelector("turbo-frame[busy]")) return true
    if (this.element.querySelector("dialog[open], [data-refresh-hold]")) return true
    if (this.focusedEditable() || this.dirtyForm()) return true

    return false
  }

  focusedEditable() {
    const active = document.activeElement
    return Boolean(active && this.element.contains(active) &&
      (active.matches("input, textarea, select") || active.isContentEditable))
  }

  dirtyForm() {
    return Array.from(this.element.querySelectorAll("form")).some((form) =>
      Array.from(form.elements).some((control) => {
        if (control.matches("input[type='checkbox'], input[type='radio']")) {
          return control.checked !== control.defaultChecked
        }
        if (control.matches("select")) {
          return Array.from(control.options).some((option) => option.selected !== option.defaultSelected)
        }
        return "defaultValue" in control && control.value !== control.defaultValue
      })
    )
  }

  listTarget() {
    const targets = this.element.querySelectorAll("#suppliers_list")
    return targets.length === 1 ? targets[0] : null
  }

  setBusy(target, busy) {
    if (!target) return
    if (busy) {
      target.dataset.activationBusy = "true"
      target.setAttribute("aria-busy", "true")
    } else {
      delete target.dataset.activationBusy
      target.removeAttribute("aria-busy")
    }
  }

}
